import { hasStatementSegmentFailed } from '@/modules/projects/status'
import type { Statement } from '@/modules/projects/stores'

export interface PageRange {
  start: number
  end: number
}

export const pageSpanLabel = (
  startPage: number | null,
  endPage: number | null,
) => {
  if (startPage == null) return 'Unknown pages'
  if (endPage == null || endPage === startPage) return `Page ${startPage}`
  return `Pages ${startPage}–${endPage}`
}

// Pages the statement has but no cycle and no excluded range covers. Built over a per-page flag so
// overlapping and non-contiguous spans (reunified cycles can overlap) never break the math.
export const uncoveredPageRanges = (statement: Statement): PageRange[] => {
  const totalPages = statement.numberOfPages
  if (!totalPages || totalPages < 1) return []

  // A legacy cycle with no page attribution covers pages we cannot place, so any gap we compute
  // would be a false alarm. Say nothing instead.
  const hasUnplacedSegment = (statement.segments ?? []).some(
    (segment) => segment.startPage == null || segment.endPage == null,
  )
  if (hasUnplacedSegment) return []

  const covered = new Array(totalPages + 1).fill(false)
  const markSpan = (startPage: number | null, endPage: number | null) => {
    if (startPage == null) return
    const firstPage = Math.max(1, startPage)
    const lastPage = Math.min(totalPages, endPage ?? startPage)
    for (let page = firstPage; page <= lastPage; page += 1) covered[page] = true
  }

  for (const segment of statement.segments ?? []) {
    markSpan(segment.startPage, segment.endPage)
  }
  for (const range of statement.excludedRanges ?? []) {
    markSpan(range.startPage, range.endPage)
  }

  const gaps: PageRange[] = []
  let runStart: number | null = null
  for (let page = 1; page <= totalPages; page += 1) {
    if (!covered[page] && runStart == null) runStart = page
    if (covered[page] && runStart != null) {
      gaps.push({ start: runStart, end: page - 1 })
      runStart = null
    }
  }
  if (runStart != null) gaps.push({ start: runStart, end: totalPages })
  return gaps
}

export const failedSegmentPageCount = (statement: Statement): number => {
  const segments = statement.segments ?? []
  if (segments.length === 0) return statement.numberOfPages

  return segments.filter(hasStatementSegmentFailed).reduce((sum, segment) => {
    if (segment.startPage == null || segment.endPage == null) return sum
    return sum + (segment.endPage - segment.startPage + 1)
  }, 0)
}
